import {Component, HostListener, Input, OnInit, ElementRef, ViewChild, TemplateRef, Output, EventEmitter} from '@angular/core';
import {HttpClient, HttpHeaders, HttpEvent, HttpEventType} from "@angular/common/http";
import {AbstractControl, FormGroup} from "@angular/forms";
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';
import { ToastrService } from 'ngx-toastr';
import {environment} from "../../../../environments/environment";
import { trigger, state, style, animate, transition } from '@angular/animations';
import { ModalComponent } from '../../common/layout/modal/modal.component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

interface UploadedFile {
  id: string;
  fileName: string;
  fileCategory: string;
  fileCategoryId: string;
  fileDisplayName: string;
  isClaimFile: boolean;
  valueInDispute?: boolean;
  claimNumber?: number;
  fileGuid?: string;
  [key: string]: any; // optional
}

interface ApiResponse<T> {
  success: boolean;
  result?: {
    files?: UploadedFile[];
    file?: UploadedFile;
    fileDeleted?: boolean;
    errorMessage?: string;
  };
  error?: {
    message: string;
  };
}

// Add a service to handle file list reloading
export class FileUploadService {
  private fileListReloadSubject = new Subject<void>();
  fileListReload$ = this.fileListReloadSubject.asObservable();

  triggerFileListReload() {
    this.fileListReloadSubject.next();
  }
}

@Component({
  selector: 'app-form-input-file',
  templateUrl: './form-input-file.component.html',
  styleUrls: ['./form-input-file.component.sass'],
  animations: [
    trigger('uploadComplete', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('uploadArea', [
      state('active', style({
        height: '*',
        opacity: 1
      })),
      state('complete', style({
        height: '0',
        opacity: 0
      })),
      transition('active => complete', [
        animate('300ms ease-out')
      ])
    ])
  ]
})
export class FormInputFileComponent implements OnInit{

  @HostListener("dragoOver", ["$event"]) dragOver(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  @HostListener("dragLeave", ["$event"]) dragLeave(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  @HostListener('drop', ['$event']) onDrop(event: any): boolean {
    return false
  }

  @Input() formName!: FormGroup;
  @Input() control!: AbstractControl;
  @Input() name!: string;
  @Input() label: string | null = '';
  @Input() fileCategory?: string;
  @Input() isClaimFile?: boolean;

  @Input() sessionId: string = '';
  @Input() projectShortName: string | null = '';
  @Input() minFiles: number = 0;
  @Input() maxFiles: number = 10;
  @Input() tokenIdentifier: string = '';
  @Input() valueInDispute = false;
  @Input() projectSettings: any = {};
  @Input() acceptedFileTypes: string = '*';
  @Input() actRequiredFileCategories: any[] = [];
  @Input() index?: number;
  @Input() disableFileGet: boolean = false;
  @Input() isDatagateMode: boolean = false;

  @Output() fileDeleted = new EventEmitter<void>();
  @Output() fileUploaded = new EventEmitter<void>();
  @Output() fileCountUpdated = new EventEmitter<void>();
  @Output() isValid = new EventEmitter<boolean>();
  @Output() disputeBoxValid = new EventEmitter<boolean>();
  @Output() progressChanged = new EventEmitter<void>();

  files: File[] = [];
  uploadedFiles: UploadedFile[] = [];
  maxFileSizeMB = 25;
  isDragging = false;

  public uploadUrl: string = '';
  public getFilesUrl: string = '';
  public deleteFileUrl: string = '';

  public uploadProgress: Record<string, number> = {};

  public isUploading: boolean = false;

  @ViewChild('uploadedFilesSection') uploadedFilesSection!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef;

  uploadAreaState = 'active';
  allUploadsComplete = false;
  uploadInProgress = false;

  @ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;
  fileToDelete: UploadedFile | null = null;
  modalRef?: BsModalRef;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit() {
    // Ensure sessionId is a string
    const sessionToken = typeof this.sessionId === 'string' ? this.sessionId : '';

    // Construct URLs with proper encoding
    if(!this.isDatagateMode) {
      const baseUrl = environment.apiUrl + 'Value/File/';
      this.uploadUrl = `${baseUrl}${encodeURIComponent(this.projectShortName || '')}/${encodeURIComponent(this.tokenIdentifier)}/${encodeURIComponent(sessionToken)}`;
      this.getFilesUrl = `${baseUrl}${encodeURIComponent(this.projectShortName || '')}/${encodeURIComponent(this.tokenIdentifier)}/${encodeURIComponent(sessionToken)}`;
      this.deleteFileUrl = `${baseUrl}${encodeURIComponent(this.projectShortName || '')}/${encodeURIComponent(this.tokenIdentifier)}/${encodeURIComponent(sessionToken)}`;
    }else{
      const baseUrl = environment.apiUrl + 'DataGate/UploadFile/';
      this.uploadUrl = `${baseUrl}${encodeURIComponent(this.tokenIdentifier)}`;
      this.getFilesUrl = `${baseUrl}${encodeURIComponent(this.tokenIdentifier)}`;
      this.deleteFileUrl = `${baseUrl}${encodeURIComponent(this.tokenIdentifier)}`;
    }


    this.fetchUploadedFiles();

    // Subscribe to file list reload events
    this.fileUploadService.fileListReload$.subscribe(() => {
      this.fetchUploadedFiles();
    });

    // In datagate mode, emit initial validation state since fetchUploadedFiles won't be called
    if (this.isDatagateMode && this.disableFileGet) {
      setTimeout(() => {
        this.updateFormControlValue();
      }, 100);
    }
  }


  onFileDrop(files: NgxFileDropEntry[]) {
    for (const droppedFile of files) {
      // Check if we've hit the maximum files limit
      if (this.files.length >= this.maxFiles) {
        this.toastr.warning(`Es dürfen maximal ${this.maxFiles} Dateien hochgeladen werden.`, 'Limit erreicht');
        break;
      }

      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          const exists = this.files.some(f => f.name === file.name && f.size === file.size);

          if (exists) {
            this.toastr.error(`Die Datei '${file.name}' wurde bereits hinzugefügt.`, 'Doppelte Datei');
            return;
          }

          if (file.size <= this.maxFileSizeMB * 1024 * 1024) {
            // Add file to array for progress tracking
            this.files.push(file);
            // Automatically start upload for this file
            this.uploadFile(file);
          } else {
            this.toastr.error(`Die Datei '${file.name}' ist größer als ${this.maxFileSizeMB} MB.`, 'Dateigröße zu groß');
          }
        });
      }
    }
  }

  public updateFormControlValue() {
    if (this.control) {
      const filteredFiles = this.filteredFiles;
      const isValid = this.isValidForStreitwert();
      const hasMin = this.hasMinimumFiles;


      // Create a custom value object
      const formValue = {
        files: filteredFiles,
        isValid: isValid,
        hasMinimumFiles: hasMin,
        count: filteredFiles.length,
        fileCategory: this.fileCategory,
        fileCategoryId: this.fileCategory,
        actual: filteredFiles.length,
        index: this.index,
        valueInDispute: this.valueInDispute
      };

      // Update the form control
      this.control.setValue(formValue);
      this.control.markAsTouched();

      // Set validation errors if minimum files requirement is not met
      if (!isValid) {
        this.control.setErrors({ 'insufficientFiles': true });
      } else {
        this.control.setErrors(null);
      }

      // Trigger validation in the next tick to avoid change detection issues
      setTimeout(() => {
        // Trigger validation
        this.control.updateValueAndValidity({ emitEvent: true });

        // Trigger parent form validation
        if (this.formName) {
          this.formName.updateValueAndValidity({ emitEvent: true });
        }

        // Emit validation state with the actual boolean value
        this.isValid.emit(isValid);

        // Emit dispute box validation state
        if (this.valueInDispute) {
          // For value-in-dispute, emit true if validation passes OR if it's an optional category (effectiveMinFiles === 0)
          const disputeBoxValid = isValid || this.effectiveMinFiles === 0;
          this.disputeBoxValid.emit(disputeBoxValid);
        }

        // Emit file count update
        this.fileCountUpdated.emit();

        // Emit file uploaded event to notify parent
        this.fileUploaded.emit();
      }, 0);
    }
  }

  private isValidForStreitwert(): boolean {


    if (!this.valueInDispute || !this.projectSettings?.streitwertRequiredFileCategories) {
      const isValid = this.filteredFiles.length >= this.minFiles;

      return isValid;
    }

    // Get required categories from Streitwert configuration
    const requiredCategories = this.projectSettings.streitwertRequiredFileCategories;


    // For value-in-dispute, we need to check if we have the minimum required files
    // for the current category only, not all categories
    if (this.fileCategory) {
      const category = requiredCategories.find((cat: any) => cat.id === this.fileCategory);


      if (category) {
        // If minimumFiles is 0, this category is optional and always valid
        if (category.minimumFiles === 0) {

          return true;
        }

        const filesForCategory = this.uploadedFiles.filter(file => {
          const matchesCategory = file.fileCategoryId === this.fileCategory;
          const matchesIndex = this.index !== undefined ? file.claimNumber === this.index : true;
          return matchesCategory && matchesIndex;
        });



        const isValid = filesForCategory.length >= category.minimumFiles;



        return isValid;
      }
    }

    // If no category found, default to valid for optional categories
    return this.effectiveMinFiles === 0;
  }

  async uploadFiles() {
    if (this.files.length === 0) return;

    this.uploadInProgress = true;
    let completedUploads = 0;
    const totalFiles = this.files.length;

    try {
      for (const file of this.files) {
        await this.uploadFile(file);
        completedUploads++;

        // Check if all uploads in this batch are complete
        if (completedUploads === totalFiles) {
          this.handleAllUploadsComplete();
        }
      }
    } catch (error) {
    } finally {
      this.uploadInProgress = false;
    }
  }

  private handleAllUploadsComplete() {
    this.files = []; // Clear the files array

    // Keep progress visible for a moment so users can see completion
    setTimeout(() => {
      this.uploadProgress = {}; // Clear upload progress after delay
      this.progressChanged.emit();
    }, 1000);

    // Update form control value to reflect the new state
    this.updateFormControlValue();

    // Only scroll if minimum requirements are met
    if (this.isValidForStreitwert()) {
      // Scroll to uploaded files section after a short delay
      setTimeout(() => {
        this.uploadedFilesSection?.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 600);
    }
  }

  private async uploadFile(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const fileContentBase64 = (reader.result as string).split(',')[1];

        // Get the file category from Streitwert configuration if in dispute mode
        let fileCategoryId = this.fileCategory;
        if (this.valueInDispute && this.projectSettings?.streitwertRequiredFileCategories?.length > 0) {
          // Find the matching category from Streitwert configuration
          const matchingCategory = this.projectSettings.streitwertRequiredFileCategories.find(
            (category: any) => category.id === this.fileCategory
          );

          if (matchingCategory) {
            fileCategoryId = matchingCategory.id;
          } else {
            // If no matching category found, use the first one
            fileCategoryId = this.projectSettings.streitwertRequiredFileCategories[0].id;
          }
        }

        if (!fileCategoryId) {
          this.toastr.error('Fehler: Keine Dateikategorie gefunden', 'Upload fehlgeschlagen');
          // Remove failed file from files array
          this.files = this.files.filter(f => f.name !== file.name || f.size !== file.size);
          reject(new Error('No file category ID provided'));
          return;
        }
        let payload: any;

        if(this.isDatagateMode) {
          payload = {
            token: this.tokenIdentifier,
            projectShortName: this.projectShortName,
            fileContent: fileContentBase64,
            fileName: file.name,
            fileCategory: fileCategoryId,
            claimNumber: this.valueInDispute ? this.index : null,
            isClaimFile: this.isClaimFile || false
          };
        }else{
          payload = {
            token: this.tokenIdentifier,
            projectShortName: this.projectShortName,
            sessionId: this.sessionId,
            fileContent: fileContentBase64,
            fileName: file.name,
            fileCategory: fileCategoryId,
            claimNumber: this.valueInDispute ? this.index : null,
            isClaimFile: this.isClaimFile || false
          };
        }


        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        // Initialize progress for this file
        this.uploadProgress[file.name] = 0;
        this.progressChanged.emit();

        // Add a small delay to show initial progress
        setTimeout(() => {
          this.uploadProgress[file.name] = 10;
          this.progressChanged.emit();
        }, 100);

        this.http.post<ApiResponse<{ file: UploadedFile }>>(this.uploadUrl, payload, {
          headers,
          reportProgress: true,
          observe: 'events'
        }).subscribe({
          next: (event: HttpEvent<any>) => {
            if (event.type === HttpEventType.UploadProgress) {
              // Calculate upload progress percentage
              if (event.total) {
                const progress = Math.round((event.loaded / event.total) * 100);
                this.uploadProgress[file.name] = Math.max(progress, this.uploadProgress[file.name] || 0);
                this.progressChanged.emit();
              }
            } else if (event.type === HttpEventType.Response) {
              // Upload completed
              this.uploadProgress[file.name] = 100;
              this.progressChanged.emit();

              // Add a small delay before showing success message
              setTimeout(() => {
                this.toastr.success(`'${file.name}' wurde hochgeladen.`, 'Upload erfolgreich');
              }, 200);

              if (event.body?.success && event.body.result?.file) {
                if (this.isDatagateMode) {
                  // Normalize the file object for Datagate mode
                  const uploadedFile = event.body.result.file;
                  uploadedFile.fileCategoryId = this.fileCategory;
                  uploadedFile.id = uploadedFile.fileGuid;
                  if (this.isClaimFile !== undefined) {
                    uploadedFile.isClaimFile = this.isClaimFile;
                  }
                  if (this.index !== undefined) {
                    uploadedFile.claimNumber = this.index;
                  }
                  this.uploadedFiles.push(uploadedFile);
                  this.uploadedFiles = [...this.uploadedFiles]; // Trigger change detection
                  this.updateFormControlValue();
                } else {
                  // Fetch the latest list from API only if disableFileGet is false
                  if (!this.disableFileGet) {
                    // Delay the fetch to allow progress bar to show completion
                    setTimeout(() => {
                      this.fetchUploadedFiles();
                    }, 1500); // Wait 1.5 seconds before clearing progress
                  } else {
                    // If disableFileGet is true, manually update the uploaded files list
                    if (event.body.result.file) {
                      this.uploadedFiles.push(event.body.result.file);
                      this.updateFormControlValue();
                    }
                  }
                  // Trigger reload for all instances
                  setTimeout(() => {
                    this.fileUploadService.triggerFileListReload();
                  }, 1500); // Delay reload to allow progress to be visible
                }
              }

              // Clear the files array and progress since upload is complete
              this.files = [];
              this.uploadProgress = {};
              this.progressChanged.emit();

              resolve();
            }
          },
          error: (error: Error) => {
            this.toastr.error(`Fehler beim Hochladen von '${file.name}'`, 'Upload fehlgeschlagen');
            // Remove progress entry and failed file from files array
            delete this.uploadProgress[file.name];
            this.files = this.files.filter(f => f.name !== file.name || f.size !== file.size);
            this.progressChanged.emit();
            reject(error);
          }
        });
      };

      reader.onerror = () => {
        this.toastr.error(`Fehler beim Lesen von '${file.name}'`, 'Lesefehler');
        // Remove failed file from files array
        this.files = this.files.filter(f => f.name !== file.name || f.size !== file.size);
        reject(reader.error);
      };

      reader.readAsDataURL(file);
    });
  }

  fetchUploadedFiles() {
    if (!this.tokenIdentifier || this.disableFileGet) {
      return;
    }

    const headers = new HttpHeaders({
      'Session-Id': this.sessionId || ''
    });

    this.http.get<ApiResponse<UploadedFile[]>>(this.getFilesUrl, {
      headers,
      observe: 'response'
    })
      .subscribe({
        next: (response) => {
          if (response.body?.success && response.body.result?.files) {
            // Update the uploaded files list
            this.uploadedFiles = response.body.result.files;
            // Clear any pending uploads
            this.uploadProgress = {};
            this.progressChanged.emit();
            this.uploadInProgress = false;
            this.isDragging = false;
            // Set upload area to active state
            this.uploadAreaState = 'active';
            // Update form control value and trigger validation
            this.updateFormControlValue();
            // Emit event to parent component to update validation
            this.fileUploaded.emit();
            // Emit validation state
            const validForStreitwert = this.isValidForStreitwert();
            this.isValid.emit(validForStreitwert);
            // Emit dispute box validation state
            if (this.valueInDispute) {
              // For value-in-dispute, emit true if validation passes OR if it's an optional category
              const disputeBoxValid = validForStreitwert || this.effectiveMinFiles === 0;
              this.disputeBoxValid.emit(disputeBoxValid);
            }
          } else {
            // Clear the file lists if no files are returned
            this.uploadedFiles = [];
            this.files = [];
            this.uploadProgress = {};
            this.progressChanged.emit();
            this.uploadInProgress = false;
            this.isDragging = false;
            // Set upload area to active state
            this.uploadAreaState = 'active';
            this.updateFormControlValue();
            this.fileUploaded.emit();
            // Emit validation state
            const validForStreitwert = this.isValidForStreitwert();
            this.isValid.emit(validForStreitwert);
            // Emit dispute box validation state
            if (this.valueInDispute) {
              // For value-in-dispute, emit true if validation passes OR if it's an optional category
              const disputeBoxValid = validForStreitwert || this.effectiveMinFiles === 0;
              this.disputeBoxValid.emit(disputeBoxValid);
            }
          }
        },
        error: (error) => {
          this.toastr.error('Fehler beim Abrufen der hochgeladenen Dateien.', 'Fehler');
          // Clear the file lists on error
          this.uploadedFiles = [];
          this.files = [];
          this.uploadProgress = {};
          this.progressChanged.emit();
          this.uploadInProgress = false;
          this.isDragging = false;
          // Set upload area to active state
          this.uploadAreaState = 'active';
          this.updateFormControlValue();
          this.fileUploaded.emit();
          // Emit validation state
          const validForStreitwert = this.isValidForStreitwert();
          this.isValid.emit(validForStreitwert);
          // Emit dispute box validation state
          if (this.valueInDispute) {
            // For value-in-dispute, emit true if validation passes OR if it's an optional category
            const disputeBoxValid = validForStreitwert || this.effectiveMinFiles === 0;
            this.disputeBoxValid.emit(disputeBoxValid);
          }
        }
      });
  }

  deleteFile(fileId: string) {
    const file = this.uploadedFiles.find(f => f.id === fileId);
    if (file) {
      this.fileToDelete = file;
      this.modalRef = this.modalService.show(this.deleteConfirmModal, {
        class: 'modal-sm'
      });
    }
  }

  confirmDelete() {
    if (
      this.fileToDelete &&
      this.projectShortName &&
      this.tokenIdentifier &&
      (!this.isDatagateMode ? this.sessionId : true)
    ) {
      let url: string;
      let headers: HttpHeaders;
      if (this.isDatagateMode) {
        // Use the Datagate delete endpoint with environment.apiUrl
        url = `${environment.apiUrl}DataGate/DeleteFile/${this.tokenIdentifier}/${this.fileToDelete.id}`;
        headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      } else {
        // Use the existing delete endpoint
        url = `${this.deleteFileUrl}/${this.fileToDelete.fileGuid}`;
        headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.tokenIdentifier}`
        });
      }

      this.http.delete<ApiResponse<UploadedFile>>(url, { headers })
        .subscribe({
          next: (response) => {
            if (response.result?.fileDeleted) {
              if (this.isDatagateMode) {
                // Reset fileToDelete before updating arrays
                const deletedId = this.fileToDelete?.id;
                this.fileToDelete = null;
                // Remove the file locally in Datagate mode
                this.uploadedFiles = this.uploadedFiles.filter(f => f.id !== deletedId);
                this.uploadedFiles = [...this.uploadedFiles]; // Force change detection
                console.debug('Deleted file id:', deletedId, 'Remaining files:', this.uploadedFiles.map(f => f.id));
                this.updateFormControlValue();
              } else {
                // Fetch the latest files from API
                this.fetchUploadedFiles();
                // Trigger reload for all instances
                this.fileUploadService.triggerFileListReload();
              }
              this.toastr.success('Die Datei wurde erfolgreich gelöscht.', 'Erfolg');
              this.fileDeleted.emit();
              // Reset upload area state
              this.uploadAreaState = 'active';
              this.allUploadsComplete = false;
              this.files = [];
              this.uploadProgress = {};
              this.progressChanged.emit();
              this.uploadInProgress = false;
              this.isDragging = false;
            } else {
              this.toastr.error(response.result?.errorMessage || 'Fehler beim Löschen der Datei.', 'Fehler');
            }
          },
          error: (error) => {
            this.toastr.error('Fehler beim Löschen der Datei.', 'Fehler');
          }
        });
      this.modalRef?.hide();
    }
  }

  cancelDelete() {
    this.modalRef?.hide();
    this.fileToDelete = null;
  }

  onManualFileSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    const fileList: FileList | null = target.files;

    if (fileList) {
      // Reset the upload area state when new files are selected
      this.uploadAreaState = 'active';
      this.uploadInProgress = false;
      this.isDragging = false;

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];

        // Check if we've hit the maximum files limit
        if (this.files.length >= this.maxFiles) {
          this.toastr.warning(`Es dürfen maximal ${this.maxFiles} Dateien hochgeladen werden.`, 'Limit erreicht');
          break;
        }

        // Check file size
        if (file.size <= this.maxFileSizeMB * 1024 * 1024) {
          const exists = this.files.some(f => f.name === file.name && f.size === file.size);
          if (!exists) {
            // Add file to array for progress tracking
            this.files.push(file);
            // Automatically start upload for this file
            this.uploadFile(file);
          } else {
            this.toastr.error(`Die Datei '${file.name}' wurde bereits hinzugefügt.`, 'Doppelte Datei');
          }
        } else {
          this.toastr.error(`Die Datei '${file.name}' ist zu groß.`, 'Maximale Größe überschritten');
        }
      }

      // Reset the file input to allow selecting the same file again
      target.value = '';
    }
  }

  get filteredFiles(): UploadedFile[] {
    let filtered = this.uploadedFiles;

    // Filter by file category if specified
    if (this.fileCategory) {
      filtered = filtered.filter(file => {
        const matches = file['fileCategory'] === this.fileCategory || file['fileCategoryId'] === this.fileCategory;
        return matches;
      });
    }

    // Filter by claim status if specified
    if (this.isClaimFile !== undefined) {
      filtered = filtered.filter(file => {
        const matches = file.isClaimFile === this.isClaimFile;
        return matches;
      });
    }

    // Additional filtering for value in dispute
    if (this.valueInDispute && this.projectSettings?.streitwertRequiredFileCategories) {
      filtered = filtered.filter(file => {
        const matches = this.projectSettings.streitwertRequiredFileCategories.some(
          (category: any) => category.id === file.fileCategoryId
        );
        return matches;
      });

      // Filter by index in dispute box mode
      if (this.index !== undefined) {
        filtered = filtered.filter(file => file.claimNumber === this.index);
      }
    }

    return filtered;
  }

  get shouldShowUploadedFiles(): boolean {
    return this.filteredFiles.length > 0;
  }

  get shouldShowUploadArea(): boolean {
    // Always show upload area unless we're currently uploading or have reached max files
    // The minimum files requirement should not hide the upload area
    return !this.uploadInProgress && this.uploadAreaState === 'active' && this.filteredFiles.length < this.maxFiles;
  }

  get effectiveMinFiles(): number {
    if (this.valueInDispute && this.projectSettings?.streitwertRequiredFileCategories) {
      // Value-in-dispute logic
      if (this.fileCategory) {
        const category = this.projectSettings.streitwertRequiredFileCategories.find(
          (cat: any) => cat.id === this.fileCategory
        );
        if (category) {
          return category.minimumFiles;
        }
      }
    } else {
      // Act component logic - check against project settings or minFiles
      if (this.projectSettings?.requiredFileCategories && this.fileCategory) {
        const category = this.projectSettings.requiredFileCategories.find(
          (cat: any) => cat.id === this.fileCategory
        );
        if (category) {
          return category.minimumFiles;
        }
      }
    }

    // Fallback to the input property
    return this.minFiles;
  }

  get hasMinimumFiles(): boolean {

    if (this.valueInDispute && this.projectSettings?.streitwertRequiredFileCategories) {
      // Value-in-dispute logic
      const requiredCategories = this.projectSettings.streitwertRequiredFileCategories;

      if (this.fileCategory) {
        const category = requiredCategories.find((cat: any) => cat.id === this.fileCategory);
        if (category) {
          // If minimumFiles is 0, this category is optional and always has minimum files
          if (category.minimumFiles === 0) {

            return true;
          }

          const filesForCategory = this.uploadedFiles.filter(file => {
            const matchesCategory = file.fileCategoryId === this.fileCategory;
            const matchesIndex = this.index !== undefined ? file.claimNumber === this.index : true;
            return matchesCategory && matchesIndex;
          });

          const hasMin = filesForCategory.length >= category.minimumFiles;

          return hasMin;
        }
      }
    } else {
      // Act component logic - check against project settings or minFiles
      let minimumRequired = this.minFiles;

      // If we have project settings with requiredFileCategories, use those
      if (this.projectSettings?.requiredFileCategories && this.fileCategory) {
        const category = this.projectSettings.requiredFileCategories.find(
          (cat: any) => cat.id === this.fileCategory
        );
        if (category) {
          minimumRequired = category.minimumFiles;
        }
      }

      const relevantFiles = this.filteredFiles.length;
      const hasMin = relevantFiles >= minimumRequired;


      return hasMin;
    }

    return false;
  }

}

